import { useState } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { CalendarIcon, FileDown } from "lucide-react"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { cn } from "@/lib/utils"
import { useCondominium } from "@/hooks/useCondominium"
import { useSuppliers } from "@/hooks/useSuppliers"
import { useFinancialReportData, useCashFlowData, useOverdueCharges, useSupplierReport } from "@/hooks/useFinancialReports"
import { 
  generateFinancialSummaryPDF, 
  generateFinancialDetailedPDF,
  generateCashFlowPDF,
  generateDefaultReportPDF,
  generateSupplierReportPDF,
  generateChargesReportPDF
} from "@/utils/pdfGenerator"
import { toast } from "sonner"

type ReportType = 'summary' | 'detailed' | 'cashflow' | 'default' | 'supplier' | 'charges'

export const RelatorioFinanceiroSelector = () => {
  const [reportType, setReportType] = useState<ReportType>('summary')
  const [startDate, setStartDate] = useState<Date>()
  const [endDate, setEndDate] = useState<Date>()
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>("")
  
  const { data: condominium } = useCondominium()
  const { data: suppliers } = useSuppliers()
  
  const { data: reportData, isLoading: isLoadingReport } = useFinancialReportData(
    startDate || new Date(),
    endDate || new Date()
  )
  
  const { data: cashFlowData, isLoading: isLoadingCashFlow } = useCashFlowData(
    startDate || new Date(),
    endDate || new Date()
  )
  
  const { data: overdueData, isLoading: isLoadingOverdue } = useOverdueCharges()
  
  const { data: supplierData, isLoading: isLoadingSupplier } = useSupplierReport(
    selectedSupplierId,
    startDate || new Date(),
    endDate || new Date()
  )

  const handleGeneratePDF = async () => {
    if (!condominium || !reportData) {
      toast.error("Dados do condomínio não encontrados")
      return
    }

    if (!startDate || !endDate) {
      toast.error("Selecione o período do relatório")
      return
    }

    const baseData = {
      condominiumName: condominium.name,
      condominiumAddress: condominium.address,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    }

    try {
      switch (reportType) {
        case 'summary':
          await generateFinancialSummaryPDF({
            ...baseData,
            totalRevenues: reportData.totalRevenues,
            totalExpenses: reportData.totalExpenses,
            balance: reportData.balance,
            chargesStats: reportData.chargesStats,
            expensesByCategory: reportData.expensesByCategory,
            revenuesByCategory: reportData.revenuesByCategory,
            topSuppliers: reportData.topSuppliers
          })
          break

        case 'detailed':
          await generateFinancialDetailedPDF({
            ...baseData,
            revenues: reportData.revenues.map(r => ({
              date: r.revenue_date,
              description: r.description,
              category: r.category,
              amount: r.amount_cents,
              payment_method: r.payment_method
            })),
            expenses: reportData.expenses.map(e => ({
              date: e.expense_date,
              description: e.description,
              category: e.category,
              supplier: e.supplier?.name,
              amount: e.amount_cents,
              status: e.status
            })),
            charges: reportData.charges.map(c => ({
              unit: c.unit_id || '',
              resident: '',
              description: c.description,
              due_date: c.due_date,
              amount: c.amount_cents,
              status: c.status
            }))
          })
          break

        case 'cashflow':
          if (!cashFlowData) {
            toast.error("Dados de fluxo de caixa não disponíveis")
            return
          }
          await generateCashFlowPDF({
            ...baseData,
            initialBalance: cashFlowData.initialBalance,
            revenues: cashFlowData.revenues.map(r => ({
              date: r.revenue_date,
              description: r.description,
              amount: r.amount_cents
            })),
            expenses: cashFlowData.expenses.map(e => ({
              date: e.expense_date,
              description: e.description,
              amount: e.amount_cents
            })),
            dailyBalance: cashFlowData.dailyBalance,
            finalBalance: cashFlowData.finalBalance
          })
          break

        case 'default':
          if (!overdueData) {
            toast.error("Dados de inadimplência não disponíveis")
            return
          }
          await generateDefaultReportPDF({
            condominiumName: condominium.name,
            condominiumAddress: condominium.address,
            reportDate: new Date().toISOString().split('T')[0],
            overdueCharges: overdueData.charges.map(c => ({
              unit: c.unit_id || '',
              resident: '',
              description: c.description,
              due_date: c.due_date,
              days_overdue: c.days_overdue,
              amount: c.amount_cents
            })),
            totalOverdue: overdueData.totalOverdue,
            totalCharges: overdueData.totalCharges,
            defaultRate: overdueData.defaultRate
          })
          break

        case 'supplier':
          if (!selectedSupplierId) {
            toast.error("Selecione um fornecedor")
            return
          }
          if (!supplierData) {
            toast.error("Dados do fornecedor não disponíveis")
            return
          }
          await generateSupplierReportPDF({
            ...baseData,
            supplier: {
              name: supplierData.supplier.name,
              document: supplierData.supplier.document,
              category: supplierData.supplier.category,
              phone: supplierData.supplier.phone,
              email: supplierData.supplier.email
            },
            expenses: supplierData.expenses.map(e => ({
              date: e.expense_date,
              description: e.description,
              amount: e.amount_cents,
              status: e.status
            })),
            totalPaid: supplierData.totalPaid,
            totalPending: supplierData.totalPending,
            serviceCount: supplierData.serviceCount,
            averageTicket: supplierData.averageTicket
          })
          break

        case 'charges':
          const collectionRate = reportData.chargesStats.paid > 0
            ? (reportData.chargesStats.paid / (reportData.chargesStats.paid + reportData.chargesStats.pending + reportData.chargesStats.overdue)) * 100
            : 0

          await generateChargesReportPDF({
            ...baseData,
            charges: reportData.charges.map(c => ({
              unit: c.unit_id || '',
              resident: '',
              description: c.description,
              due_date: c.due_date,
              amount: c.amount_cents,
              status: c.status,
              paid_date: c.paid_at
            })),
            stats: reportData.chargesStats,
            collectionRate
          })
          break
      }

      toast.success("Relatório gerado com sucesso!")
    } catch (error) {
      console.error("Erro ao gerar relatório:", error)
      toast.error("Erro ao gerar relatório PDF")
    }
  }

  const isLoading = isLoadingReport || isLoadingCashFlow || isLoadingOverdue || isLoadingSupplier

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label>Tipo de Relatório</Label>
            <Select value={reportType} onValueChange={(v) => setReportType(v as ReportType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="summary">Resumo Executivo</SelectItem>
                <SelectItem value="detailed">Relatório Completo (Analítico)</SelectItem>
                <SelectItem value="cashflow">Fluxo de Caixa</SelectItem>
                <SelectItem value="default">Inadimplência</SelectItem>
                <SelectItem value="supplier">Por Fornecedor</SelectItem>
                <SelectItem value="charges">Cobranças por Período</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {reportType === 'supplier' && (
            <div className="space-y-2">
              <Label>Fornecedor</Label>
              <Select value={selectedSupplierId} onValueChange={setSelectedSupplierId}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um fornecedor" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers?.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {reportType !== 'default' && (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Data Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP", { locale: ptBR }) : "Selecione"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Data Fim</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP", { locale: ptBR }) : "Selecione"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>
        )}

        <Button 
          onClick={handleGeneratePDF} 
          disabled={isLoading || (reportType !== 'default' && (!startDate || !endDate))}
          className="w-full"
        >
          <FileDown className="h-4 w-4 mr-2" />
          Gerar Relatório PDF
        </Button>
      </CardContent>
    </Card>
  )
}
